import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CreateInvestmentThesisSchema, UpdateInvestmentThesisSchema } from 'shared';
import type { UpdateInvestmentThesisInput } from 'shared';
import { toast } from 'sonner';

import { InvestmentAllocationCard } from '../components/domain/InvestmentAllocationCard';
import { InvestmentIdentityConfirmation } from '../components/domain/InvestmentIdentityConfirmation';
import { InvestmentPortfolioDashboard } from '../components/domain/InvestmentPortfolioDashboard';
import { InvestmentThesisForm } from '../components/domain/InvestmentThesisForm';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { useAccounts } from '../features/accounts/queries';
import {
  useCreateInvestmentThesis,
  useCreateInvestmentThesisSuccessor,
  useRebindInvestmentThesis,
  useUpdateInvestmentThesis,
} from '../features/investmentTheses/mutations';
import {
  useInvestmentAllocation,
  useInvestmentHoldings,
  useInvestmentTheses,
} from '../features/investmentTheses/queries';
import type { InvestmentHoldingDto, InvestmentThesisDto } from '../lib/api/endpoints';

const matchLabels = {
  MATCHED: 'Thesis linked',
  THESIS_MISSING: 'Thesis missing',
  IDENTITY_AMBIGUOUS: 'Thesis missing: identity ambiguous',
  IDENTITY_REVIEW_REQUIRED: 'Thesis missing: identity confirmation required',
} as const;

export function Investing() {
  const accounts = useAccounts();
  const investmentAccounts = (accounts.data ?? []).filter(
    (account) =>
      account.mandate === 'LONG_TERM_INVESTING' || (account.investmentThesisCount ?? 0) > 0,
  );
  const [searchParams] = useSearchParams();
  const linkedAccountId = searchParams.get('accountId');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const accountId =
    selectedAccountId ??
    investmentAccounts.find((account) => account.id === linkedAccountId)?.id ??
    investmentAccounts[0]?.id ??
    null;
  const selectedAccount = investmentAccounts.find((account) => account.id === accountId) ?? null;
  const canMutate = selectedAccount?.mandate === 'LONG_TERM_INVESTING';
  const holdings = useInvestmentHoldings(canMutate ? accountId : null);
  const allocation = useInvestmentAllocation(canMutate ? accountId : null);
  const theses = useInvestmentTheses(accountId, true);
  const [selectedHoldingId, setSelectedHoldingId] = useState<string | null>(null);
  const [rebindHoldingId, setRebindHoldingId] = useState<string | null>(null);
  const [manualEditor, setManualEditor] = useState<'new' | string | null>(null);
  const [manualIdentity, setManualIdentity] = useState('');
  const [manualSymbol, setManualSymbol] = useState('');
  const selectedHolding =
    holdings.data?.holdings.find((holding) => holding.id === selectedHoldingId) ?? null;
  const manualTheses = (theses.data ?? []).filter((thesis) => thesis.identity.source === 'MANUAL');
  const recordedTheses = canMutate
    ? (theses.data ?? []).filter(
        (thesis) => thesis.identity.source === 'MANUAL' || thesis.status === 'CLOSED',
      )
    : (theses.data ?? []);
  const selectedManualThesis =
    manualEditor && manualEditor !== 'new'
      ? (manualTheses.find((thesis) => thesis.id === manualEditor) ?? null)
      : null;
  const createThesis = useCreateInvestmentThesis();
  const updateThesis = useUpdateInvestmentThesis();
  const rebindThesis = useRebindInvestmentThesis();
  const createSuccessor = useCreateInvestmentThesisSuccessor();

  function saveThesis(holding: InvestmentHoldingDto, input: UpdateInvestmentThesisInput): void {
    if (holding.thesis) {
      const parsed = UpdateInvestmentThesisSchema.safeParse(input);
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? 'Check the thesis fields.');
        return;
      }
      updateThesis.mutate(
        { id: holding.thesis.id, input: parsed.data },
        { onSuccess: () => toast.success('Investment thesis saved.') },
      );
      return;
    }
    if (!accountId) return;
    const parsed = CreateInvestmentThesisSchema.safeParse({
      ...input,
      accountId,
      identity: { source: 'HL_CODE', value: holding.code },
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Check the thesis fields.');
      return;
    }
    createThesis.mutate(parsed.data, {
      onSuccess: () => toast.success('Investment thesis draft created.'),
    });
  }

  function saveManualThesis(input: UpdateInvestmentThesisInput): void {
    if (selectedManualThesis) {
      const parsed = UpdateInvestmentThesisSchema.safeParse(input);
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? 'Check the thesis fields.');
        return;
      }
      updateThesis.mutate(
        { id: selectedManualThesis.id, input: parsed.data },
        { onSuccess: () => toast.success('Investment thesis saved.') },
      );
      return;
    }
    if (!accountId || !canMutate) return;
    const parsed = CreateInvestmentThesisSchema.safeParse({
      ...input,
      accountId,
      identity: {
        source: 'MANUAL',
        value: manualIdentity,
        displaySymbol: manualSymbol,
      },
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Check the thesis fields.');
      return;
    }
    createThesis.mutate(parsed.data, {
      onSuccess: () => {
        toast.success('Manual investment thesis created.');
        setManualEditor(null);
        setManualIdentity('');
        setManualSymbol('');
      },
    });
  }

  function changeStatus(
    thesis: InvestmentThesisDto,
    status: UpdateInvestmentThesisInput['status'],
  ) {
    if (!status) return;
    updateThesis.mutate(
      { id: thesis.id, input: { status } },
      { onSuccess: () => toast.success(`Thesis moved to ${status.replaceAll('_', ' ')}.`) },
    );
  }

  function lifecycleActions(thesis: InvestmentThesisDto) {
    if (!canMutate || thesis.status === 'CLOSED') return null;
    return (
      <div className="flex flex-wrap gap-2">
        {thesis.status === 'DRAFT' || thesis.status === 'UNDER_REVIEW' ? (
          <Button size="sm" onClick={() => changeStatus(thesis, 'ACTIVE')}>
            Activate
          </Button>
        ) : null}
        {thesis.status === 'ACTIVE' || thesis.status === 'BROKEN' ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => changeStatus(thesis, 'UNDER_REVIEW')}
          >
            Mark under review
          </Button>
        ) : null}
        {thesis.status === 'ACTIVE' || thesis.status === 'UNDER_REVIEW' ? (
          <Button size="sm" variant="secondary" onClick={() => changeStatus(thesis, 'BROKEN')}>
            Mark broken
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => changeStatus(thesis, 'CLOSED')}>
          Close
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={createSuccessor.isPending}
          onClick={() => {
            if (!window.confirm('Close this thesis and start a new draft?')) return;
            createSuccessor.mutate(
              { id: thesis.id, input: {} },
              { onSuccess: () => toast.success('New thesis draft started.') },
            );
          }}
        >
          Start new thesis
        </Button>
      </div>
    );
  }

  if (accounts.isLoading) {
    return <main className="p-6 text-text-muted">Loading investments...</main>;
  }
  if (accounts.isError) {
    return <main className="p-6 text-danger">Could not load investment accounts.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Investing</h1>
            <p className="mt-1 text-sm text-text-muted">
              See combined long-term exposure by theme, then manage each account&apos;s allocation
              and investment theses.
            </p>
          </div>
          {investmentAccounts.length > 0 ? (
            <label className="text-sm text-text-muted">
              Account
              <select
                aria-label="Investment account"
                className="mt-1 block h-10 w-full rounded border border-border bg-bg-muted px-3 text-text sm:w-64"
                value={accountId ?? ''}
                onChange={(event) => {
                  setSelectedAccountId(event.target.value);
                  setSelectedHoldingId(null);
                  setRebindHoldingId(null);
                  setManualEditor(null);
                }}
              >
                {investmentAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                    {account.mandate === 'LONG_TERM_INVESTING' ? '' : ' (historical theses)'}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </header>

        {investmentAccounts.some((account) => account.mandate === 'LONG_TERM_INVESTING') ? (
          <InvestmentPortfolioDashboard
            onSelectAccount={(id) => {
              setSelectedAccountId(id);
              setSelectedHoldingId(null);
              setRebindHoldingId(null);
              setManualEditor(null);
              document
                .getElementById('investment-account-details')
                ?.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        ) : null}
        <h2 id="investment-account-details" className="text-xl font-semibold">
          Account details{selectedAccount ? ` - ${selectedAccount.name}` : ''}
        </h2>

        {investmentAccounts.length === 0 ? (
          <Card>
            <h2 className="text-lg font-semibold">No Long-Term Investing accounts</h2>
            <p className="mt-2 text-sm text-text-muted">
              Create or classify an account before recording investment theses.
            </p>
            <Button asChild className="mt-4" variant="secondary">
              <Link to="/settings">Open settings</Link>
            </Button>
          </Card>
        ) : theses.isLoading || (canMutate && holdings.isLoading) ? (
          <p className="text-sm text-text-muted">Loading holdings...</p>
        ) : theses.isError || (canMutate && holdings.isError) ? (
          <p className="text-sm text-danger">Could not load long-term holdings.</p>
        ) : (
          <>
            {canMutate ? (
              <InvestmentAllocationCard
                allocation={allocation.data}
                isError={allocation.isError}
                isLoading={allocation.isLoading}
              />
            ) : null}
            {!canMutate ? (
              <Card>
                <h2 className="text-lg font-semibold">Historical investment theses</h2>
                <p className="mt-2 text-sm text-text-muted">
                  This account is no longer classified as Long-Term Investing. Preserved theses
                  remain readable, but they cannot be changed or superseded.
                </p>
              </Card>
            ) : holdings.data?.holdings.length ? (
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
                <section className="space-y-3" aria-label="Long-term holdings">
                  {holdings.data.holdings.map((holding) => {
                    const linkedThesis = holding.thesis;
                    const candidate = theses.data?.find(
                      (thesis) =>
                        thesis.status !== 'CLOSED' &&
                        thesis.identity.source === 'HL_CODE' &&
                        thesis.identity.value === holding.code,
                    );
                    return (
                      <Card key={holding.id} className="p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="font-semibold">{holding.symbol}</div>
                            <div className="truncate text-sm text-text-muted">{holding.stock}</div>
                            <div className="mt-2 text-sm tabular-nums">
                              {holding.units} units · {holdings.data.currency} {holding.value}
                            </div>
                            <div
                              className={
                                holding.thesisMatchStatus === 'MATCHED'
                                  ? 'mt-2 text-sm text-success'
                                  : 'mt-2 text-sm text-warn'
                              }
                            >
                              {matchLabels[holding.thesisMatchStatus]}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {holding.thesisMatchStatus === 'IDENTITY_REVIEW_REQUIRED' &&
                            candidate ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setRebindHoldingId(holding.id)}
                              >
                                Review identity
                              </Button>
                            ) : null}
                            {holding.thesis || holding.thesisMatchStatus === 'THESIS_MISSING' ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setSelectedHoldingId(holding.id)}
                              >
                                {holding.thesis ? 'Edit thesis' : 'Create thesis'}
                              </Button>
                            ) : null}
                          </div>
                        </div>
                        {rebindHoldingId === holding.id && candidate ? (
                          <InvestmentIdentityConfirmation
                            holding={holding}
                            thesis={candidate}
                            isPending={rebindThesis.isPending}
                            onCancel={() => setRebindHoldingId(null)}
                            onConfirm={(reason) =>
                              rebindThesis.mutate(
                                {
                                  id: candidate.id,
                                  input: {
                                    identity: { source: 'HL_CODE', value: holding.code },
                                    confirmedSameInvestment: true,
                                    reason,
                                  },
                                },
                                {
                                  onSuccess: () => {
                                    setRebindHoldingId(null);
                                    toast.success('Investment identity confirmed.');
                                  },
                                },
                              )
                            }
                          />
                        ) : null}
                        {linkedThesis ? (
                          <div className="mt-4 border-t border-border pt-3">
                            {lifecycleActions(linkedThesis)}
                          </div>
                        ) : null}
                      </Card>
                    );
                  })}
                </section>

                <Card header={<h2 className="text-lg font-semibold">Investment thesis</h2>}>
                  {selectedHolding ? (
                    <InvestmentThesisForm
                      key={`${selectedHolding.id}:${selectedHolding.thesis?.updatedAt ?? 'new'}`}
                      security={{
                        displaySymbol: selectedHolding.symbol,
                        description: selectedHolding.stock,
                      }}
                      thesis={selectedHolding.thesis}
                      isPending={createThesis.isPending || updateThesis.isPending}
                      idPrefix={`holding-${selectedHolding.id}`}
                      onSave={(input) => saveThesis(selectedHolding, input)}
                    />
                  ) : (
                    <p className="text-sm text-text-muted">
                      Select a holding to create or edit its thesis.
                    </p>
                  )}
                </Card>
              </div>
            ) : (
              <Card>
                <h2 className="text-lg font-semibold">No imported HL holdings</h2>
                <p className="mt-2 text-sm text-text-muted">
                  You can create a manual thesis below without an HL snapshot. A manual identity
                  must be entered explicitly and is not inferred from its display symbol.
                </p>
                {selectedAccount?.broker === 'HL' ? (
                  <Button asChild className="mt-4" variant="secondary">
                    <Link to="/import">Import holdings</Link>
                  </Button>
                ) : null}
              </Card>
            )}

            <Card
              header={
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold">
                    {canMutate ? 'Recorded investment theses' : 'Preserved investment theses'}
                  </h2>
                  {canMutate ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setManualEditor('new');
                        setManualIdentity('');
                        setManualSymbol('');
                      }}
                    >
                      New manual thesis
                    </Button>
                  ) : null}
                </div>
              }
            >
              {recordedTheses.length > 0 ? (
                <div className="space-y-3">
                  {recordedTheses.map((thesis) => (
                    <div key={thesis.id} className="border-b border-border pb-3 last:border-0">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold">{thesis.displaySymbol}</div>
                          <div className="text-sm text-text-muted">
                            {thesis.identity.source === 'MANUAL' ? 'Manual identity' : 'HL code'}:{' '}
                            {thesis.identity.value}
                          </div>
                          <div className="mt-1 text-sm">{thesis.status.replaceAll('_', ' ')}</div>
                        </div>
                        {canMutate &&
                        thesis.identity.source === 'MANUAL' &&
                        thesis.status !== 'CLOSED' ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setManualEditor(thesis.id)}
                          >
                            Edit thesis
                          </Button>
                        ) : null}
                      </div>
                      {canMutate && thesis.identity.source === 'MANUAL' ? (
                        <div className="mt-3">{lifecycleActions(thesis)}</div>
                      ) : null}
                      {thesis.thesisSummary ? (
                        <p className="mt-2 text-sm text-text-muted">{thesis.thesisSummary}</p>
                      ) : (
                        <p className="mt-2 text-sm text-text-muted">No thesis summary recorded.</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-text-muted">No investment theses recorded.</p>
              )}

              {canMutate && manualEditor ? (
                <div className="mt-5 space-y-4 border-t border-border pt-5">
                  {manualEditor === 'new' ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="text-sm text-text-muted" htmlFor="manual-identity">
                        Security identity or reference
                        <Input
                          id="manual-identity"
                          value={manualIdentity}
                          onChange={(event) => setManualIdentity(event.target.value)}
                        />
                      </label>
                      <label className="text-sm text-text-muted" htmlFor="manual-symbol">
                        Display symbol
                        <Input
                          id="manual-symbol"
                          value={manualSymbol}
                          onChange={(event) => setManualSymbol(event.target.value)}
                        />
                      </label>
                    </div>
                  ) : null}
                  <InvestmentThesisForm
                    key={selectedManualThesis?.updatedAt ?? 'new-manual-thesis'}
                    security={{
                      displaySymbol: selectedManualThesis?.displaySymbol ?? manualSymbol,
                      description:
                        selectedManualThesis?.identity.value ??
                        manualIdentity ??
                        'Manual investment identity',
                    }}
                    thesis={selectedManualThesis}
                    isPending={createThesis.isPending || updateThesis.isPending}
                    idPrefix={`manual-${selectedManualThesis?.id ?? 'new'}`}
                    onSave={saveManualThesis}
                  />
                </div>
              ) : null}
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
